// quant.ts — the web client's decoder for OSPQ, the quantized splat-diff
// format of the live WebSocket's compressed encoding (ARCHITECTURE.md §7.1).
// It must agree with core/splat/quant.go exactly: the client holds the same
// integer state as the server, so every diff applies without drift.
//
//   "OSPQ" | version u8=1 | flags u8 (bit 0 reset) | 2 reserved bytes
//   uvarint nUpdated | uvarint nRemoved
//   updated ids, removed ids: ascending, uvarint first then (id[i]-id[i-1]-1)
//   nUpdated mask bytes (bit g = parameter group g changed)
//   per component k (pos 3, logScale 3, rot 4, color 3, opacity 1), per
//     updated splat whose mask has k's group: zigzag varint (q_new - q_base)
//   per updated splat with the label bit: uvarint label
//
// Values are q · QUANT_STEPS[k]; the steps are powers of two, so the float32
// result is exact.

import { emptyCloud, FormatError } from './format';
import type { SplatCloud, SplatDelta } from './format';

export const QUANT_MAGIC = 'OSPQ';
export const QUANT_VERSION = 1;
export const QFLAG_RESET = 1;
export const QCOMPS = 14;
export const QGROUP_LABEL = 5;

// Component → parameter group (pos, scale, rot, color, opacity).
const GROUP_OF = [0, 0, 0, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 4];

// Steps per component (core/splat QuantSteps): position 2^-15 m, log-scale
// 2^-8, rotation 2^-10, colour and opacity 2^-8.
export const QUANT_STEPS = [
  2 ** -15, 2 ** -15, 2 ** -15,
  2 ** -8, 2 ** -8, 2 ** -8,
  2 ** -10, 2 ** -10, 2 ** -10, 2 ** -10,
  2 ** -8, 2 ** -8, 2 ** -8,
  2 ** -8,
];

// QState is a quantized cloud sorted by ascending unique id: q holds QCOMPS
// int32 components per splat.
export interface QState {
  count: number;
  ids: Uint32Array;
  q: Int32Array;
  label: Uint32Array;
}

export function emptyQState(count = 0): QState {
  return { count, ids: new Uint32Array(count), q: new Int32Array(count * QCOMPS), label: new Uint32Array(count) };
}

class Reader {
  off: number;
  constructor(private b: Uint8Array, off: number) {
    this.off = off;
  }
  // uvarint reads an unsigned LEB128 value of at most 53 bits.
  uvarint(): number {
    let v = 0;
    let mul = 1;
    for (let i = 0; i < 8; i++) {
      if (this.off >= this.b.length) throw new FormatError(`OSPQ: varint runs past the end (byte ${this.off})`);
      const c = this.b[this.off++];
      v += (c & 0x7f) * mul;
      if (c < 0x80) return v;
      mul *= 128;
    }
    throw new FormatError(`OSPQ: varint too long at byte ${this.off}`);
  }
  // varint reads a zigzag-signed value.
  varint(): number {
    const u = this.uvarint();
    return u % 2 === 0 ? u / 2 : -(u + 1) / 2;
  }
  count(): number {
    const v = this.uvarint();
    if (v > this.b.length - this.off) throw new FormatError(`OSPQ: count ${v} exceeds ${this.b.length - this.off} remaining bytes`);
    return v;
  }
  ids(n: number): Uint32Array {
    const out = new Uint32Array(n);
    let last = -1;
    for (let i = 0; i < n; i++) {
      const id = last + 1 + this.uvarint();
      if (id > 0xffffffff) throw new FormatError(`OSPQ: id ${id} overflows uint32`);
      out[i] = id;
      last = id;
    }
    return out;
  }
}

export interface QApplied {
  state: QState; // the new state
  reset: boolean; // the payload replaced the state (a snapshot)
  updated: QState; // the added or changed splats, id order
  removed: Uint32Array;
}

// applyOSPQ applies an OSPQ payload to base (ignored for a reset payload).
// base is not modified.
export function applyOSPQ(b: Uint8Array, base: QState | null): QApplied {
  if (b.length < 8) throw new FormatError(`OSPQ: ${b.length} bytes is shorter than the 8-byte header`);
  const magic = String.fromCharCode(b[0], b[1], b[2], b[3]);
  if (magic !== QUANT_MAGIC) throw new FormatError(`OSPQ: bad magic ${JSON.stringify(magic)}`);
  if (b[4] !== QUANT_VERSION) throw new FormatError(`OSPQ: unsupported version ${b[4]}`);
  const reset = (b[5] & QFLAG_RESET) !== 0;
  const bs = reset || !base ? emptyQState() : base;
  const r = new Reader(b, 8);
  const nu = r.count();
  const nr = r.count();
  const updIds = r.ids(nu);
  const remIds = r.ids(nr);
  if (r.off + nu > b.length) throw new FormatError('OSPQ: masks run past the end');
  const masks = b.subarray(r.off, r.off + nu);
  r.off += nu;

  // Each updated splat starts from its base (zero when new).
  const upd = emptyQState(nu);
  upd.ids.set(updIds);
  let bi = 0;
  for (let i = 0; i < nu; i++) {
    const id = updIds[i];
    while (bi < bs.count && bs.ids[bi] < id) bi++;
    if (bi < bs.count && bs.ids[bi] === id) {
      upd.q.set(bs.q.subarray(bi * QCOMPS, bi * QCOMPS + QCOMPS), i * QCOMPS);
      upd.label[i] = bs.label[bi];
    }
  }
  for (let k = 0; k < QCOMPS; k++) {
    const bit = 1 << GROUP_OF[k];
    for (let i = 0; i < nu; i++) {
      if (masks[i] & bit) {
        const v = upd.q[i * QCOMPS + k] + r.varint();
        if (v < -2147483648 || v > 2147483647) throw new FormatError('OSPQ: component overflows int32');
        upd.q[i * QCOMPS + k] = v;
      }
    }
  }
  for (let i = 0; i < nu; i++) if (masks[i] & (1 << QGROUP_LABEL)) upd.label[i] = r.uvarint();
  if (r.off !== b.length) throw new FormatError(`OSPQ: ${b.length - r.off} trailing bytes`);

  // Merge base (minus removals) with the updates, both id-sorted.
  const out = emptyQState(bs.count + nu);
  let w = 0, i = 0, ri = 0, ui = 0;
  const put = (src: QState, j: number) => {
    out.ids[w] = src.ids[j];
    out.q.set(src.q.subarray(j * QCOMPS, j * QCOMPS + QCOMPS), w * QCOMPS);
    out.label[w] = src.label[j];
    w++;
  };
  while (i < bs.count || ui < nu) {
    if (ui >= nu || (i < bs.count && bs.ids[i] < upd.ids[ui])) {
      const id = bs.ids[i];
      while (ri < nr && remIds[ri] < id) ri++;
      if (!(ri < nr && remIds[ri] === id)) put(bs, i);
      i++;
      continue;
    }
    if (i < bs.count && bs.ids[i] === upd.ids[ui]) i++;
    put(upd, ui++);
  }
  const state: QState = { count: w, ids: out.ids.subarray(0, w), q: out.q.subarray(0, w * QCOMPS), label: out.label.subarray(0, w) };
  return { state, reset, updated: upd, removed: remIds };
}

// dequantize returns q as a float SplatCloud (id order).
export function dequantize(s: QState): SplatCloud {
  const c = emptyCloud(s.count);
  c.ids.set(s.ids);
  c.label.set(s.label);
  for (let i = 0; i < s.count; i++) {
    const q = s.q, o = i * QCOMPS;
    for (let k = 0; k < 3; k++) c.pos[i * 3 + k] = q[o + k] * QUANT_STEPS[k];
    for (let k = 0; k < 3; k++) c.logScale[i * 3 + k] = q[o + 3 + k] * QUANT_STEPS[3 + k];
    for (let k = 0; k < 4; k++) c.rot[i * 4 + k] = q[o + 6 + k] * QUANT_STEPS[6 + k];
    for (let k = 0; k < 3; k++) c.color[i * 3 + k] = q[o + 10 + k] * QUANT_STEPS[10 + k];
    c.opacity[i] = q[o + 13] * QUANT_STEPS[13];
  }
  return c;
}

// QDecoder keeps one connection's quantized state and turns each OSPQ
// payload into what the plain encoding delivers: a full cloud for a reset,
// otherwise a SplatDelta of the changed splats (full values) and removals.
export class QDecoder {
  private state: QState | null = null;

  get count(): number {
    return this.state?.count ?? 0;
  }

  // apply returns { snapshot } for a reset payload and { delta } otherwise.
  // A diff that arrives before any reset is applied on an empty state.
  apply(payload: Uint8Array): { snapshot?: SplatCloud; delta?: SplatDelta } {
    const res = applyOSPQ(payload, this.state);
    this.state = res.state;
    if (res.reset) return { snapshot: dequantize(res.state) };
    return { delta: { updated: dequantize(res.updated), removed: res.removed } };
  }

  reset(): void {
    this.state = null;
  }
}
